// web/history.ts — COLONY HISTORY: a persistent lineage logbook.
//
// BROOD is a persistent game, so the colony deserves a remembered past. This is an
// OBSERVED event log: each tick-batch it diffs the live sim against the last sample
// and records meaningful moments — major mutations, gene discoveries, region
// captures, apex-kill milestones, extinctions (rival colonies AND your own castes),
// and leaderboard milestones. Major moments also capture a SNAPSHOT (a summary of
// the colony at that instant) so the player can revisit the first colony, an extinct
// caste, or a turning point months later.
//
// It is a LOGBOOK, not a deterministic derivation: it's observed during live play
// and SAVED verbatim (capped), then restored on load — so replay never rewrites the
// past. Math.random is not used here; events are read straight off real state.

import { CASTE_NAME, PLAYER_COLONY } from './game.ts';
import type { GameSession } from './game.ts';
import { RARITY_NAME, GENE_TYPE_NAME } from '../src/sim/geneData.ts';

export type HistoryEventType =
  | 'founding' | 'mutation' | 'discovery' | 'region' | 'apex' | 'extinction' | 'milestone'
  | 'boom' | 'victory';

export const EVENT_META: Record<HistoryEventType, { icon: string; label: string }> = {
  founding: { icon: '🏛️', label: 'Founding' },
  mutation: { icon: '🧬', label: 'Mutation' },
  discovery: { icon: '🔬', label: 'Discovery' },
  region: { icon: '🗺️', label: 'Region' },
  apex: { icon: '⚔️', label: 'Apex kill' },
  extinction: { icon: '💀', label: 'Extinction' },
  milestone: { icon: '🏆', label: 'Milestone' },
  boom: { icon: '📈', label: 'Population boom' },
  victory: { icon: '🐲', label: 'Apex victory' },
};

// --- pure narrative layer (UNIT-TESTABLE: no session, no DOM, no RNG) ---------

export interface NarrativeCtx {
  gen: number;
  region?: string; // region name
  apex?: string; // apex creature name
  population?: number;
  from?: number; // population before a crash
  value?: number; // generic count (kills milestone, grew-to N)
  subject?: string; // caste name, gene label, etc.
  first?: boolean; // first-of-kind flag (first territory, first kill)
}

/**
 * Deterministic variant pick: the GENERATION number selects the phrasing, so the same
 * event always narrates identically (replay-safe, and stored narratives never drift) while
 * different generations rotate through the pool — a long chronicle reads less repetitively.
 * Each pool's variants are kept on-topic (same subjects/numbers) so callers/tests can rely
 * on the facts regardless of which phrasing is chosen.
 */
function pick(pool: readonly string[], g: number): string {
  const n = pool.length;
  return pool[((g % n) + n) % n] as string;
}

/**
 * A short single sentence in the brood's voice, generation-forward. Deterministic: a pure
 * function of `type`, the fields present in `ctx`, and `ctx.gen` (the variant selector).
 * No RNG, no state.
 */
export function narrate(type: HistoryEventType, ctx: NarrativeCtx): string {
  const G = ctx.gen;
  switch (type) {
    case 'founding':
      return pick([
        `The brood takes root — Generation ${G} of a new colony.`,
        `Generation ${G} carves out a new nest; the colony begins.`,
        `From a lone queen, Generation ${G} founds the brood.`,
      ], G);
    case 'region':
      return ctx.first
        ? pick([
            `Generation ${G} established the first ${ctx.region} outpost.`,
            `The colony's reach touched the ${ctx.region} for the first time under Generation ${G}.`,
          ], G)
        : pick([
            `Generation ${G} extended the colony's reach into the ${ctx.region}.`,
            `Generation ${G} pressed the brood deeper into the ${ctx.region}.`,
          ], G);
    case 'apex':
      return ctx.first
        ? pick([
            `Generation ${G} drew first blood — the colony's first kill.`,
            `The brood tastes victory: Generation ${G} makes its first kill.`,
          ], G)
        : pick([
            `Generation ${G} marked ${ctx.value} foes fallen.`,
            `${ctx.value} foes have fallen to the brood by Generation ${G}.`,
          ], G);
    case 'victory':
      return pick([
        `Generation ${G} brought down the ${ctx.apex} — a legend the colony will not forget.`,
        `The ${ctx.apex} falls to Generation ${G}; the chronicle records a legend.`,
      ], G);
    case 'boom':
      return pick([
        `A brood-boom under Generation ${G} swelled the colony to ${ctx.population}.`,
        `Generation ${G} flourished — the colony surged to ${ctx.population}.`,
      ], G);
    case 'extinction':
      if (ctx.from !== undefined)
        return pick([
          `A great culling cut the colony from ${ctx.from} to ${ctx.population}, but Generation ${G} endured.`,
          `Generation ${G} weathered a collapse from ${ctx.from} down to ${ctx.population}.`,
        ], G);
      if (ctx.subject !== undefined)
        return pick([
          `The last ${ctx.subject} of the brood has fallen under Generation ${G}.`,
          `Generation ${G} mourns its final ${ctx.subject}.`,
        ], G);
      return pick([
        `Generation ${G} saw the colony wiped out — yet the lineage remembers.`,
        `The brood is undone under Generation ${G}, but its lineage is not forgotten.`,
      ], G);
    case 'discovery':
      return pick([
        `Generation ${G} brought forth ${ctx.subject}.`,
        `Generation ${G} unlocked ${ctx.subject}.`,
        `A new strain emerges: Generation ${G} yields ${ctx.subject}.`,
      ], G);
    case 'milestone':
      if (ctx.subject === 'rank1')
        return pick([
          `Generation ${G} climbed to the top of the standings.`,
          `Generation ${G} seized first place among all colonies.`,
        ], G);
      if (ctx.value !== undefined)
        return pick([
          `Generation ${G} grew the colony to ${ctx.value}.`,
          `The colony reached ${ctx.value} strong under Generation ${G}.`,
        ], G);
      return `Generation ${G}: ${ctx.subject ?? 'a new chapter'}.`;
    case 'mutation':
    default:
      return `Generation ${G}: ${ctx.subject ?? 'a new chapter'}.`;
  }
}

/** The chronicle line to display: the narrative if present, else the legacy detail. */
export function chronicleText(e: HistoryEvent): string {
  return e.narrative ?? e.detail;
}

// --- population-trajectory detectors (PURE, UNIT-TESTABLE) --------------------

export const BOOM_FACTOR = 1.5;
export const BOOM_MIN_GAIN = 12;
export const CRASH_FACTOR = 0.6;
export const CRASH_MIN_DROP = 12;

/** A surge: population is ≥ factor× the recent trough AND gained ≥ min bodies. */
export function isBoom(trough: number, pop: number): boolean {
  return pop >= Math.ceil(trough * BOOM_FACTOR) && pop - trough >= BOOM_MIN_GAIN;
}

/** A crash: population fell to ≤ factor× the recent peak AND lost ≥ min bodies. */
export function isCrash(peak: number, pop: number): boolean {
  return pop <= Math.floor(peak * CRASH_FACTOR) && peak - pop >= CRASH_MIN_DROP;
}

/** The player colony's apex slayings (deterministic; reads resolved defeat log). */
export function playerVictories(session: GameSession): ApexVictory[] {
  return session
    .apexDefeats(50)
    .filter((d) => d.outcome === 'slain' && d.victor === PLAYER_COLONY)
    .map((d) => ({ tick: d.tick, name: d.name }));
}

interface ApexVictory {
  tick: number;
  name: string;
}

/** A captured summary of the colony at a moment — the "revisit" payload. */
export interface Snapshot {
  population: number;
  worker: number;
  soldier: number;
  flyer: number;
  specialist: number;
  genes: number;
  topGene: string;
  maxAscension: number;
  regionsHeld: number;
  rank: number;
  of: number;
  military: number;
  hives: number;
}

export interface HistoryEvent {
  tick: number;
  type: HistoryEventType;
  title: string;
  detail: string;
  /** Major moments are never dropped by the cap. */
  pinned?: boolean;
  /** Present on major moments — a revisitable summary of the colony then. */
  snapshot?: Snapshot | undefined;
  /** The player colony's generation when this happened. Optional: old saves lack it. */
  gen?: number;
  /** Generation-stamped prose for the chronicle. Optional: old saves lack it. */
  narrative?: string;
}

const MAX_EVENTS = 400;
const KILL_MILESTONES: readonly number[] = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];
const POP_STEP = 10;

export class ColonyHistory {
  private events: HistoryEvent[] = [];
  private seeded = false;
  private loaded = false;

  // --- baselines (diffed each observe) ---
  private prevKills = 0;
  private nextKillIdx = 0;
  private everCaptured = new Set<number>();
  private prevCaste = [0, 0, 0, 0];
  private casteEverSeen = [false, false, false, false];
  private loggedCasteExtinct = [false, false, false, false];
  private loggedExtinctMask = 0;
  private prevUnlocked = 0;
  private bestRank = 99;
  private peakPop = 0;
  private peakPopMilestone = 0;
  private seenRarity = -1;
  private seenTypeMask = 0;
  private playerFell = false;
  private firstKillLogged = false;
  private firstTerritoryLogged = false;
  private boomTrough = 0;
  private crashPeak = 0;
  // Keyed on `${tick}:${name}` (NOT tick alone) so two distinct apex slayings that
  // resolve on the SAME tick are both logged, not deduped into one.
  private loggedApexVictories = new Set<string>();

  static fromJSON(saved: readonly HistoryEvent[]): ColonyHistory {
    const h = new ColonyHistory();
    h.events = saved.map((e) => ({ ...e }));
    h.loaded = true;
    return h;
  }

  toJSON(): HistoryEvent[] {
    return this.events;
  }

  list(): readonly HistoryEvent[] {
    return this.events;
  }

  snapshots(): HistoryEvent[] {
    return this.events.filter((e) => e.snapshot !== undefined);
  }

  count(): number {
    return this.events.length;
  }

  // --- observation -----------------------------------------------------------

  /** Diff the live sim and append any new events. Cheap enough to run at HUD rate. */
  observe(session: GameSession): void {
    if (!this.seeded) {
      if (!this.loaded) {
        this.stamp(session, {
          tick: 0, type: 'founding', title: 'Colony founded', pinned: true,
          detail: `A new brood takes root (seed ${session.params.seed}).`,
          narrative: narrate('founding', { gen: session.playerGeneration() }),
          snapshot: this.summary(session),
        });
      }
      this.seedBaselines(session);
      this.seeded = true;
      return;
    }

    const tick = session.tick;
    const info = session.playerInfo();

    // Caste extinctions (revisitable).
    for (let c = 0; c < 4; c++) {
      const cur = casteCount(info, c);
      if (cur > 0) this.casteEverSeen[c] = true;
      if ((this.prevCaste[c] as number) > 0 && cur === 0 && this.casteEverSeen[c] && !this.loggedCasteExtinct[c]) {
        this.loggedCasteExtinct[c] = true;
        this.stamp(session, {
          tick, type: 'extinction', pinned: true,
          title: `${CASTE_NAME[c] as string}s went extinct`,
          detail: `The last ${CASTE_NAME[c] as string} of your colony has died.`,
          narrative: narrate('extinction', { gen: session.playerGeneration(), subject: CASTE_NAME[c] as string }),
          snapshot: this.summary(session),
        });
      }
      this.prevCaste[c] = cur;
    }

    // Region conquests — only the FIRST capture of each region (territory flips a
    // lot in the wars; the history records milestones, not every skirmish). The
    // very first territory the colony ever takes is a pinned, first-of-kind moment.
    for (const r of session.regionViews()) {
      if (r.controlledByPlayer && !this.everCaptured.has(r.region)) {
        this.everCaptured.add(r.region);
        const first = !this.firstTerritoryLogged;
        this.firstTerritoryLogged = true;
        this.stamp(session, {
          tick, type: 'region', pinned: true,
          title: first ? `First territory: ${r.name}` : `Captured ${r.name}`,
          detail: `Your colony first seized the ${r.name}.`,
          narrative: narrate('region', { gen: session.playerGeneration(), region: r.name, first }),
        });
      }
    }

    // First predator kill — the colony's first blood (pinned).
    const kills = session.signals().kills;
    if (!this.firstKillLogged && kills > 0) {
      this.firstKillLogged = true;
      this.stamp(session, {
        tick, type: 'apex', pinned: true, title: 'First blood',
        detail: 'Your colony scored its first kill.',
        narrative: narrate('apex', { gen: session.playerGeneration(), first: true }),
      });
    }

    // Apex-kill milestones.
    while (this.nextKillIdx < KILL_MILESTONES.length && kills >= (KILL_MILESTONES[this.nextKillIdx] as number)) {
      const n = KILL_MILESTONES[this.nextKillIdx] as number;
      this.stamp(session, {
        tick, type: 'apex', pinned: n >= 100, title: `${n} kills`,
        detail: `Your colony has slain ${n} enemies.`,
        narrative: narrate('apex', { gen: session.playerGeneration(), value: n }),
      });
      this.nextKillIdx++;
    }

    // Population trajectory — booms and major crashes off the player pop. The pure
    // helpers (isBoom/isCrash) hold the thresholds; the trough/peak reset-on-fire
    // prevents re-logging the same swing, while min/max tracking arms the next one.
    const pop = info.population;
    if (isCrash(this.crashPeak, pop)) {
      this.stamp(session, {
        tick, type: 'extinction', pinned: true, title: 'Major extinction',
        detail: `Population crashed from ${this.crashPeak} to ${pop}.`,
        narrative: narrate('extinction', { gen: session.playerGeneration(), from: this.crashPeak, population: pop }),
        snapshot: this.summary(session),
      });
      this.crashPeak = pop;
    } else {
      this.crashPeak = Math.max(this.crashPeak, pop);
    }
    if (isBoom(this.boomTrough, pop)) {
      this.stamp(session, {
        tick, type: 'boom', pinned: true, title: 'Population boom',
        detail: `The colony surged to ${pop}.`,
        narrative: narrate('boom', { gen: session.playerGeneration(), population: pop }),
      });
      this.boomTrough = pop;
    } else {
      this.boomTrough = Math.min(this.boomTrough, pop);
    }

    // Apex victories — each player slaying of an apex creature (pinned, once per kill).
    for (const v of playerVictories(session)) {
      const key = `${v.tick}:${v.name}`;
      if (this.loggedApexVictories.has(key)) continue;
      this.loggedApexVictories.add(key);
      this.stamp(session, {
        tick, type: 'victory', pinned: true, title: `Slew the ${v.name}`,
        detail: `Your colony defeated the apex ${v.name}.`,
        narrative: narrate('victory', { gen: session.playerGeneration(), apex: v.name }),
        snapshot: this.summary(session),
      });
    }

    // Rival colony extinctions — each rival's FIRST fall only (colonies can revive
    // then die again; we record the historical event once per rival).
    const exMask = session.colonyExtinctMask();
    for (let cid = 1; cid < 31; cid++) {
      const bit = 1 << cid;
      if ((exMask & bit) !== 0 && (this.loggedExtinctMask & bit) === 0) {
        this.loggedExtinctMask |= bit;
        this.stamp(session, { tick, type: 'extinction', title: `Rival colony ${cid} fell`, detail: 'A competing brood has gone extinct.' });
      }
    }

    // The player's own colony falling (and recovering — so a later fall logs again).
    if (session.playerExtinct()) {
      if (!this.playerFell) {
        this.stamp(session, {
          tick, type: 'extinction', pinned: true, title: 'Your colony has fallen',
          detail: 'Your brood was wiped out — but the lineage remembers.',
          narrative: narrate('extinction', { gen: session.playerGeneration() }),
          snapshot: this.summary(session),
        });
        this.playerFell = true;
      }
    } else {
      this.playerFell = false;
    }

    // Region unlocks.
    const unlocked = session.signals().regionsUnlocked;
    if (unlocked > this.prevUnlocked) {
      this.stamp(session, { tick, type: 'milestone', title: 'New region unlocked', detail: `The world opens up — ${unlocked} regions now reachable.` });
      this.prevUnlocked = unlocked;
    }

    // Leaderboard climbs.
    const { rank, of } = session.playerRank();
    if (rank < this.bestRank) {
      this.bestRank = rank;
      this.stamp(session, {
        tick, type: 'milestone', pinned: rank === 1,
        title: rank === 1 ? 'Reached #1' : `Climbed to #${rank}`,
        detail: `Your colony now ranks #${rank} of ${of}.`,
        narrative: narrate('milestone', { gen: session.playerGeneration(), subject: rank === 1 ? 'rank1' : `rank ${rank}` }),
        snapshot: rank === 1 ? this.summary(session) : undefined,
      });
    }

    // Population peaks.
    if (info.population > this.peakPop) {
      this.peakPop = info.population;
      if (info.population >= this.peakPopMilestone + POP_STEP) {
        this.peakPopMilestone = Math.floor(info.population / POP_STEP) * POP_STEP;
        this.stamp(session, {
          tick, type: 'milestone', pinned: this.peakPopMilestone % 50 === 0,
          title: `Colony grew to ${this.peakPopMilestone}`,
          detail: `Population reached a new high of ${info.population}.`,
          narrative: narrate('milestone', { gen: session.playerGeneration(), value: this.peakPopMilestone }),
        });
      }
    }

    // Gene discoveries (first of each rarity / type).
    const stats = session.geneStats();
    if (stats.maxRarity > this.seenRarity) {
      for (let r = this.seenRarity + 1; r <= stats.maxRarity; r++) {
        this.stamp(session, {
          tick, type: 'discovery', pinned: r >= 4,
          title: `First ${RARITY_NAME[r] as string} gene`,
          detail: `Your colony discovered a ${RARITY_NAME[r] as string}-tier gene.`,
          narrative: narrate('discovery', { gen: session.playerGeneration(), subject: `a ${RARITY_NAME[r] as string}-tier gene` }),
        });
      }
      this.seenRarity = stats.maxRarity;
    }
    const newTypes = stats.typeMask & ~this.seenTypeMask;
    if (newTypes !== 0) {
      for (let t = 0; t < GENE_TYPE_NAME.length; t++) {
        if ((newTypes & (1 << t)) !== 0) {
          this.stamp(session, {
            tick, type: 'discovery',
            title: `Discovered ${GENE_TYPE_NAME[t] as string} genes`,
            detail: `A new ${GENE_TYPE_NAME[t] as string} gene entered your pool.`,
            narrative: narrate('discovery', { gen: session.playerGeneration(), subject: `${GENE_TYPE_NAME[t] as string} genes` }),
          });
        }
      }
      this.seenTypeMask |= stats.typeMask;
    }

    this.cap();
  }

  // --- player-action records (called from main.ts) ---------------------------

  recordAscension(session: GameSession, message: string): void {
    const title = message.replace(/^Ascended →\s*/, 'Ascended to ');
    this.stamp(session, {
      tick: session.tick, type: 'mutation', pinned: !message.includes('+'),
      title, detail: 'A gene evolved up its ladder.',
      narrative: narrate('mutation', { gen: session.playerGeneration(), subject: title }),
      snapshot: message.includes('+') ? undefined : this.summary(session),
    });
  }

  recordFusion(session: GameSession, message: string): void {
    const legendary = message.includes('Legendary');
    this.stamp(session, {
      tick: session.tick, type: 'mutation', pinned: legendary, title: 'Genes fused',
      detail: message,
      narrative: narrate('mutation', { gen: session.playerGeneration(), subject: 'a gene fusion' }),
      snapshot: legendary ? this.summary(session) : undefined,
    });
  }

  recordBoxOpen(session: GameSession, tier: number, tierName: string): void {
    if (tier < 3) return; // only Apex / Evolutionary Miracle are history-worthy
    this.stamp(session, {
      tick: session.tick, type: 'milestone', pinned: true,
      title: `Opened ${tierName} Box`, detail: 'A rare evolution box yielded its prize.',
      narrative: narrate('milestone', { gen: session.playerGeneration(), subject: `the ${tierName} Box` }),
      snapshot: this.summary(session),
    });
  }

  // --- internals -------------------------------------------------------------

  /** Fill gen + a fallback narrative (if absent) from the live colony, then append. */
  private stamp(session: GameSession, e: HistoryEvent): void {
    if (e.gen === undefined) e.gen = session.playerGeneration();
    if (e.narrative === undefined) e.narrative = narrate(e.type, { gen: e.gen, subject: e.title });
    this.push(e);
  }

  private push(e: HistoryEvent): void {
    this.events.push(e);
    this.cap();
  }

  private cap(): void {
    while (this.events.length > MAX_EVENTS) {
      const i = this.events.findIndex((e) => !e.pinned);
      if (i < 0) break;
      this.events.splice(i, 1);
    }
  }

  private summary(session: GameSession): Snapshot {
    const info = session.playerInfo();
    const { rank, of } = session.playerRank();
    const stats = session.geneStats();
    return {
      population: info.population,
      worker: info.castes.worker,
      soldier: info.castes.soldier,
      flyer: info.castes.flyer,
      specialist: info.castes.specialist,
      genes: info.genes,
      topGene: session.topGeneLabel(),
      maxAscension: stats.maxAscension,
      regionsHeld: info.regionsHeld,
      rank,
      of,
      military: info.military,
      hives: info.hives,
    };
  }

  private seedBaselines(session: GameSession): void {
    const info = session.playerInfo();
    this.prevKills = session.signals().kills;
    this.nextKillIdx = 0;
    while (this.nextKillIdx < KILL_MILESTONES.length && this.prevKills >= (KILL_MILESTONES[this.nextKillIdx] as number)) this.nextKillIdx++;
    this.everCaptured = new Set(session.regionViews().filter((r) => r.controlledByPlayer).map((r) => r.region));
    for (let c = 0; c < 4; c++) { this.prevCaste[c] = casteCount(info, c); this.casteEverSeen[c] = (this.prevCaste[c] as number) > 0; }
    this.loggedExtinctMask = session.colonyExtinctMask();
    this.prevUnlocked = session.signals().regionsUnlocked;
    this.bestRank = session.playerRank().rank;
    this.peakPop = info.population;
    this.peakPopMilestone = Math.floor(info.population / POP_STEP) * POP_STEP;
    const stats = session.geneStats();
    this.seenRarity = stats.maxRarity;
    this.seenTypeMask = stats.typeMask;
    this.playerFell = session.playerExtinct();
    // New detectors: baseline off current state so a fresh load never re-logs the past.
    this.firstKillLogged = session.signals().kills > 0;
    this.firstTerritoryLogged = this.everCaptured.size > 0;
    this.boomTrough = info.population;
    this.crashPeak = info.population;
    this.loggedApexVictories = new Set(playerVictories(session).map((v) => `${v.tick}:${v.name}`));
  }
}

function casteCount(info: ReturnType<GameSession['playerInfo']>, c: number): number {
  return c === 0 ? info.castes.worker : c === 1 ? info.castes.soldier : c === 2 ? info.castes.flyer : info.castes.specialist;
}
